import { startCase } from 'lodash';
import { useMemo, VFC } from 'react';

import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionGetAnalyticsMetricV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import ToggleSlider from 'design-system/components/ToggleSlider';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

enum MetricType {
  Inquiries = 'inquiries',
  Verifications = 'verifications',
}
export enum GroupBy {
  Accounts = 'accounts',
  Inquiries = 'inquiries',
}
export enum Metric {
  InquiriesCreated = 'inquiries.created',
  InquiriesFinishedRate = 'inquiries.finished_rate',
  InquiriesInteractedRate = 'inquiries.interacted_rate',
  InquiriesRejectedRate = 'inquiries.rejected_rate',
  InquiriesStartedRate = 'inquiries.started_rate',
  InquiriesSuccessRate = 'inquiries.success_rate',
}

const METRIC_LABELS: Record<Metric, string> = {
  [Metric.InquiriesCreated]: 'Inquiry Created Volume',
  [Metric.InquiriesFinishedRate]: 'Inquiry Finished Rate',
  [Metric.InquiriesInteractedRate]: 'Inquiry Interacted Rate',
  [Metric.InquiriesRejectedRate]: 'Inquiry Rejected Rate',
  [Metric.InquiriesStartedRate]: 'Inquiry Started Rate',
  [Metric.InquiriesSuccessRate]: 'Inquiry Success Rate',
};

const groupByOptionsByMetricType: Record<MetricType, GroupBy> = {
  [MetricType.Inquiries]: GroupBy.Accounts,
  [MetricType.Verifications]: GroupBy.Inquiries,
};

const metricOptions: { label: string; value: string }[] = Object.entries(METRIC_LABELS).map(
  ([value, label]) => ({
    label,
    value,
  }),
);

interface GetAnalyticsMetricV1Props {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionGetAnalyticsMetricV1['params'];
  inquiryTemplates: WorkflowActionMeta['inquiry-templates'];
  updateActionParams: (newParams: {
    'inquiry-template-tokens'?: string[];
    metric: string;
    'group-by'?: string;
  }) => void;
}

const GetAnalyticsMetricV1: VFC<GetAnalyticsMetricV1Props> = ({
  editable,
  errors,
  inquiryTemplates,
  params,
  updateActionParams,
}) => {
  const hasInquiryTemplatePerm = usePermission(Permission.InquiryTemplateView);
  const isMetricSelected = Object.keys(METRIC_LABELS).includes(params['metric']);
  const metricType = isMetricSelected ? params['metric'].split('.')[0] : '';
  const groupByLabel = `Group By ${startCase(
    groupByOptionsByMetricType[metricType as MetricType],
  )}`;

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionGetAnalyticsMetricV1['params']>(errors);

  const templateFilterOptions = useMemo(
    () =>
      inquiryTemplates.map((template) => ({
        label: template.name,
        value: template.token,
      })),
    [inquiryTemplates],
  );

  const handleMetricChange = (value: string) => {
    updateActionParams({
      ...params,
      metric: value,
    });
  };

  const handleGroupByChange = (toggle: boolean) => {
    updateActionParams({
      ...params,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'group-by': toggle ? groupByOptionsByMetricType[metricType as MetricType] : null,
    });
  };

  const handleInquiryTemplate = (inquiryTemplateIds: string[]) => {
    updateActionParams({
      ...params,
      'inquiry-template-tokens': inquiryTemplateIds,
    });
  };

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Metric' htmlFor='params-monitor-analytics-metric__metric-input' />
      <ReactSelect
        value={params['metric']}
        options={metricOptions}
        onChange={handleMetricChange}
        disabled={!editable}
        id='params-monitor-analytics-metric__metric'
        hasError={hasErrorFor('metric')}
      />
      {renderErrorMessageFor('metric')}
      {isMetricSelected && (
        <>
          <Spacer height={2} />
          <ParamLabel
            labelText='Inquiry Template'
            htmlFor='params-monitor-analytics__inquiry-templates-input'
            helpText={
              hasInquiryTemplatePerm
                ? undefined
                : 'Your role does not have permissions to view inquiry templates.'
            }
          />
          <ReactMultiSelect
            placeholderGenerator={generatePlaceHolderDisplay({
              label: { single: 'Inquiry Template', multiple: 'Inquiry Templates' },
              emptyIsAll: true,
            })}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            value={params['inquiry-template-tokens']}
            options={templateFilterOptions}
            onChange={handleInquiryTemplate}
            disabled={!editable || !hasInquiryTemplatePerm}
            selectAllEnabled
            searchEnabled
            id='params-monitor-analytics__inquiry-templates'
            inputId='params-monitor-analytics__inquiry-templates-input'
            placeholder={hasInquiryTemplatePerm ? undefined : 'RESTRICTED'}
          />
          <Spacer height={2} />
          <ToggleSlider
            id='params-monitor-analytics-metric_group-by-input'
            value={params['group-by'] != null}
            labelOn={groupByLabel}
            labelOff={groupByLabel}
            onChange={handleGroupByChange}
          />
        </>
      )}
    </>
  );
};

export default GetAnalyticsMetricV1;
